import {
  MAILERLITE_CONTACT_FIELDS,
  MAILERLITE_OPERATORS_BY_TYPE,
  MAILERLITE_OPERATOR_LABELS,
  MAILERLITE_VALUELESS_OPERATORS,
  type MailerliteFilterCondition,
  type MailerliteFilterField,
  type MailerliteFilterGroup,
  type MailerliteFilterNode,
  type MailerliteOperator,
} from '@catalyst-psa/shared';
import { Button } from '../../components/ui/Button.js';

const FIELD_BY_KEY = new Map<string, MailerliteFilterField>(
  MAILERLITE_CONTACT_FIELDS.map((field) => [field.key, field]),
);

/** A fresh condition defaulting to the first operator the field supports. */
function newCondition(fieldKey = MAILERLITE_CONTACT_FIELDS[0]!.key): MailerliteFilterCondition {
  const field = FIELD_BY_KEY.get(fieldKey)!;
  return { kind: 'condition', field: fieldKey, operator: MAILERLITE_OPERATORS_BY_TYPE[field.type][0]! };
}

function newGroup(): MailerliteFilterGroup {
  return { kind: 'group', match: 'all', children: [newCondition()] };
}

/**
 * Recursive AND/OR filter builder. Each group renders its own match toggle and can hold
 * conditions or further groups, so arbitrarily nested boolean logic is expressible.
 */
export function MailerliteFilterBuilder({
  value,
  onChange,
  disabled,
}: {
  value: MailerliteFilterGroup;
  onChange: (next: MailerliteFilterGroup) => void;
  disabled?: boolean;
}): JSX.Element {
  return <GroupEditor group={value} onChange={onChange} onRemove={null} depth={0} disabled={disabled} />;
}

function GroupEditor({
  group,
  onChange,
  onRemove,
  depth,
  disabled,
}: {
  group: MailerliteFilterGroup;
  onChange: (next: MailerliteFilterGroup) => void;
  onRemove: (() => void) | null;
  depth: number;
  disabled?: boolean;
}): JSX.Element {
  const setChild = (index: number, child: MailerliteFilterNode): void => {
    const children = [...group.children];
    children[index] = child;
    onChange({ ...group, children });
  };
  const removeChild = (index: number): void => {
    onChange({ ...group, children: group.children.filter((_, i) => i !== index) });
  };

  return (
    <div
      className={`rounded-lg border p-3 ${
        depth === 0 ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={group.match}
          disabled={disabled}
          onChange={(e) => onChange({ ...group, match: e.target.value as 'all' | 'any' })}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="all">Match ALL of</option>
          <option value="any">Match ANY of</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={group.not === true}
            disabled={disabled}
            onChange={(e) => onChange({ ...group, not: e.target.checked || undefined })}
          />
          NOT
        </label>
        <span className="text-xs text-slate-400">
          {group.children.length === 0 ? 'empty — matches every contact' : null}
        </span>
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => onChange({ ...group, children: [...group.children, newCondition()] })}
          >
            + Rule
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => onChange({ ...group, children: [...group.children, newGroup()] })}
          >
            + Group
          </Button>
          {onRemove ? (
            <Button size="sm" variant="danger" disabled={disabled} onClick={onRemove}>
              Remove
            </Button>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        {group.children.map((child, index) => (
          <div key={index} className="flex items-start gap-2">
            <span className="mt-2 w-10 shrink-0 text-xs font-semibold uppercase text-slate-400">
              {index === 0 ? '' : group.match === 'any' ? 'or' : 'and'}
            </span>
            <div className="flex-1">
              {child.kind === 'group' ? (
                <GroupEditor
                  group={child}
                  onChange={(next) => setChild(index, next)}
                  onRemove={() => removeChild(index)}
                  depth={depth + 1}
                  disabled={disabled}
                />
              ) : (
                <ConditionEditor
                  condition={child}
                  onChange={(next) => setChild(index, next)}
                  onRemove={() => removeChild(index)}
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConditionEditor({
  condition,
  onChange,
  onRemove,
  disabled,
}: {
  condition: MailerliteFilterCondition;
  onChange: (next: MailerliteFilterCondition) => void;
  onRemove: () => void;
  disabled?: boolean;
}): JSX.Element {
  const field = FIELD_BY_KEY.get(condition.field) ?? MAILERLITE_CONTACT_FIELDS[0]!;
  const operators = MAILERLITE_OPERATORS_BY_TYPE[field.type];
  const needsValue = !MAILERLITE_VALUELESS_OPERATORS.includes(condition.operator);

  const changeField = (key: string): void => {
    const next = FIELD_BY_KEY.get(key)!;
    const operator = MAILERLITE_OPERATORS_BY_TYPE[next.type].includes(condition.operator)
      ? condition.operator
      : MAILERLITE_OPERATORS_BY_TYPE[next.type][0]!;
    onChange({ kind: 'condition', field: key, operator });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2">
      <select
        value={field.key}
        disabled={disabled}
        onChange={(e) => changeField(e.target.value)}
        className="rounded border border-slate-300 px-2 py-1 text-sm"
      >
        {MAILERLITE_CONTACT_FIELDS.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        value={condition.operator}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...condition, operator: e.target.value as MailerliteOperator, value: null })
        }
        className="rounded border border-slate-300 px-2 py-1 text-sm"
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {MAILERLITE_OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {needsValue ? (
        <ValueEditor field={field} condition={condition} onChange={onChange} disabled={disabled} />
      ) : null}

      <button
        type="button"
        disabled={disabled}
        onClick={onRemove}
        className="ml-auto rounded px-2 text-sm text-slate-400 hover:text-red-600 disabled:opacity-50"
        title="Remove this rule"
      >
        ×
      </button>
    </div>
  );
}

function ValueEditor({
  field,
  condition,
  onChange,
  disabled,
}: {
  field: MailerliteFilterField;
  condition: MailerliteFilterCondition;
  onChange: (next: MailerliteFilterCondition) => void;
  disabled?: boolean;
}): JSX.Element {
  const inputClass = 'rounded border border-slate-300 px-2 py-1 text-sm';

  if (condition.operator === 'in' && field.options) {
    const selected = condition.values ?? [];
    return (
      <div className="flex flex-wrap gap-2">
        {field.options.map((option) => (
          <label key={option.value} className="flex items-center gap-1 text-sm text-slate-700">
            <input
              type="checkbox"
              disabled={disabled}
              checked={selected.includes(option.value)}
              onChange={(e) =>
                onChange({
                  ...condition,
                  values: e.target.checked
                    ? [...selected, option.value]
                    : selected.filter((v) => v !== option.value),
                })
              }
            />
            {option.label}
          </label>
        ))}
      </div>
    );
  }

  if (field.options) {
    return (
      <select
        value={condition.value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
        className={inputClass}
      >
        <option value="">Select…</option>
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  const inputType = field.type === 'date' ? 'date' : 'text';
  return (
    <>
      <input
        type={inputType}
        value={condition.value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange({ ...condition, value: e.target.value })}
        placeholder="value"
        className={inputClass}
      />
      {condition.operator === 'between' ? (
        <>
          <span className="text-sm text-slate-500">and</span>
          <input
            type={inputType}
            value={condition.valueTo ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...condition, valueTo: e.target.value })}
            className={inputClass}
          />
        </>
      ) : null}
    </>
  );
}
